import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  AlertTriangle, BarChart3, Briefcase, CalendarClock, ChevronRight, FileSignature,
  RefreshCw, Trophy, Users, Wallet,
} from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Skeleton } from "../components/ui/skeleton";
import { apiFetch } from "../utils/api";
import { formatCurrency, formatNumber } from "../utils/formatters";
import { puoVedereAreaCommerciale, eDirezione } from "../utils/areaCommerciale";
import {
  Badge, BarraScadenza, EmptyState, ICON_TILES, InfoHint, InfoTooltip, MonthPicker,
  PRIMARY_BTN_SM, SECONDARY_BTN, SectionCard, UserAvatar, toneRuolo,
} from "../components/designSystem";

// Area commerciale: il cruscotto di chi vende. Tutti i numeri arrivano già
// calcolati da GET /area-commerciale (backend/routes/area_commerciale.py),
// che decide anche cosa si può vedere: qui non si ripetono i controlli.

const TEAM = "__team__";

const euro = (v) => formatCurrency(v || 0, { decimals: 0 });

const MESI = ["gennaio", "febbraio", "marzo", "aprile", "maggio", "giugno",
  "luglio", "agosto", "settembre", "ottobre", "novembre", "dicembre"];
const nomeMese = (ym) => `${MESI[Number(ym.slice(5, 7)) - 1]} ${ym.slice(0, 4)}`;
const meseBreve = (ym) => MESI[Number(ym.slice(5, 7)) - 1].slice(0, 3);
const meseCorrente = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};
const data = (iso) => (iso ? new Date(iso).toLocaleDateString("it-IT") : "—");
const giorni = (n) => (n == null ? "" : n === 0 ? "oggi" : n === 1 ? "da 1 giorno" : `da ${formatNumber(n)} giorni`);

const TIPI = { primo: "Nuovo cliente", aggiuntivo: "Aggiuntivo", rinnovo: "Rinnovo" };

const VOCI_PROVVIGIONE = {
  sales_commission: { label: "Commissione vendita", desc: "Percentuale del canone mensile dei clienti che hai portato, maturata solo quando il cliente ha pagato il mese." },
  sales_enrollment: { label: "Iscrizione", desc: "Compenso una tantum per ogni nuovo cliente, maturato nel mese in cui paga la prima attivazione." },
  commercial_direction: { label: "Direzione commerciale", desc: "Percentuale sul fatturato incassato di tutti i clienti." },
};

// ==================== Componenti ====================

function Kpi({ label, hint, valore, sotto, testId }) {
  return (
    <div className="bg-white border border-[#e9e9f1] rounded-[18px] shadow-[0_2px_12px_rgba(0,0,0,0.03)] px-5 py-4 min-w-0" data-testid={testId}>
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-semibold text-[#9095a1]">
        {label}
        {hint && <InfoHint title={label} description={hint} />}
      </div>
      <div className="text-[22px] font-bold scheda-font-mono text-[#15161c] mt-1.5 truncate">{valore}</div>
      {sotto && <div className="text-[12px] text-[#9095a1] mt-0.5 truncate">{sotto}</div>}
    </div>
  );
}

function Delta({ ora, prima }) {
  if (!prima && !ora) return null;
  if (!prima) return <span className="text-[#0f7040] font-semibold">nuovo rispetto al mese prima</span>;
  const pct = Math.round(((ora - prima) / prima) * 100);
  const colore = pct > 0 ? "text-[#0f7040]" : pct < 0 ? "text-[#c0392b]" : "text-[#9095a1]";
  return <span className={`font-semibold ${colore}`}>{pct > 0 ? "+" : ""}{pct}% sul mese prima</span>;
}

// Valore firmato per mese: una sola serie, quindi niente legenda (la nomina il
// titolo). Il mese scelto è in accento ed è l'unico con l'importo scritto;
// gli altri si leggono al passaggio del mouse.
function Andamento({ serie, mese }) {
  const max = Math.max(1, ...serie.map((m) => m.valore));
  if (!serie.some((m) => m.firmati > 0)) {
    return <EmptyState boxed>Nessun contratto firmato negli ultimi 12 mesi.</EmptyState>;
  }
  return (
    <div className="flex items-end gap-[2px] h-[180px] pt-6" data-testid="area-comm-andamento">
      {serie.map((m) => {
        const attivo = m.mese === mese;
        const h = m.valore > 0 ? Math.max(4, Math.round((m.valore / max) * 130)) : 0;
        return (
          <InfoTooltip
            key={m.mese}
            title={nomeMese(m.mese)}
            description={`${formatNumber(m.firmati)} ${m.firmati === 1 ? "contratto firmato" : "contratti firmati"} · ${euro(m.valore)}`}
            block
          >
            <div className="group relative flex-1 h-full flex flex-col items-center justify-end cursor-default px-[3px]" data-testid={`area-comm-bar-${m.mese}`}>
              {attivo && m.valore > 0 && (
                <span className="absolute text-[11px] font-semibold scheda-font-mono text-[#15161c] whitespace-nowrap" style={{ bottom: h + 24 }}>
                  {euro(m.valore)}
                </span>
              )}
              <div
                className={`w-full max-w-[28px] rounded-t-[4px] scheda-bar-grow transition-colors ${attivo ? "bg-[#5b5bd6]" : "bg-[#c9c9f0] group-hover:bg-[#a9a9e8]"}`}
                style={{ height: h }}
              />
              <div className="w-full h-px bg-[#e9e9f1]" />
              <span className={`text-[10.5px] mt-1.5 ${attivo ? "font-semibold text-[#15161c]" : "text-[#9095a1]"}`}>{meseBreve(m.mese)}</span>
            </div>
          </InfoTooltip>
        );
      })}
    </div>
  );
}

function Provvigioni({ p }) {
  if (p.errore) {
    return <EmptyState boxed>Non è stato possibile calcolare le provvigioni di questo mese.</EmptyState>;
  }
  const voci = Object.entries(p.per_voce || {}).filter(([, v]) => v > 0);
  return (
    <div className="flex flex-col gap-4" data-testid="area-comm-provvigioni">
      <div className="flex items-end gap-6 flex-wrap">
        <div>
          <div className="text-[10.5px] font-semibold uppercase tracking-wide text-[#9095a1]">Maturate</div>
          <div className="text-[30px] leading-none font-bold scheda-font-mono text-[#0f7040] mt-1">{euro(p.maturato + (p.totale_recuperi || 0))}</div>
        </div>
        {p.in_attesa_incasso > 0 && (
          <div>
            <div className="flex items-center gap-1 text-[10.5px] font-semibold uppercase tracking-wide text-[#9095a1]">
              In attesa di incasso
              <InfoHint title="In attesa di incasso" description="Provvigioni che matureranno quando i clienti avranno pagato il mese." />
            </div>
            <div className="text-[18px] font-bold scheda-font-mono text-[#b45309] mt-1">{euro(p.in_attesa_incasso)}</div>
          </div>
        )}
      </div>
      {voci.length === 0 && !p.totale_recuperi ? (
        <EmptyState>Nessuna provvigione maturata in questo mese.</EmptyState>
      ) : (
        <div className="flex flex-col gap-1.5">
          {voci.map(([k, v]) => (
            <div key={k} className="flex items-center gap-2 text-[13px]">
              <span className="text-[#3c414d]">{VOCI_PROVVIGIONE[k]?.label || k}</span>
              {VOCI_PROVVIGIONE[k] && <InfoHint title={VOCI_PROVVIGIONE[k].label} description={VOCI_PROVVIGIONE[k].desc} />}
              <span className="ml-auto font-semibold scheda-font-mono text-[#15161c]">{euro(v)}</span>
            </div>
          ))}
          {p.totale_recuperi > 0 && (
            <div className="flex items-center gap-2 text-[13px]">
              <span className="text-[#3c414d]">Recuperi</span>
              <InfoHint title="Recuperi" description="Provvigioni di mesi precedenti, maturate ora perché il cliente ha pagato in ritardo." />
              <span className="ml-auto font-semibold scheda-font-mono text-[#15161c]">{euro(p.totale_recuperi)}</span>
            </div>
          )}
        </div>
      )}
      {p.per_cliente?.length > 0 && (
        <div className="flex flex-col border-t border-[#f0f0f6] pt-3">
          <div className="text-[10.5px] font-semibold uppercase tracking-wide text-[#9095a1] mb-1.5">Per cliente</div>
          {p.per_cliente.map((r, i) => (
            <div key={`${r.client_id}-${r.role}-${i}`} className="flex items-center gap-2 py-1.5 text-[12.5px]">
              <span className="truncate text-[#15161c]">{r.client_name}</span>
              <span className="text-[#9095a1] shrink-0">· {VOCI_PROVVIGIONE[r.role]?.label || r.role}</span>
              <span className="ml-auto scheda-font-mono text-[#3c414d] shrink-0">{euro(r.amount)}</span>
            </div>
          ))}
        </div>
      )}
      <div className="text-[11.5px] text-[#9095a1]">
        {p.fonte === "snapshot"
          ? "Mese chiuso: importi definitivi, come nei Compensi."
          : "Mese in corso: gli importi si aggiornano man mano che i clienti pagano."}
      </div>
    </div>
  );
}

// Una riga delle "Cose da seguire": cliente, cosa manca, azione.
function RigaAzione({ r, dettaglio, badge, azione, mostraAutore, autori }) {
  return (
    <div className="flex items-center gap-3 px-3 py-2.5 rounded-[10px] hover:bg-[#fbfbfd] border-b border-[#f0f0f6] last:border-b-0 scheda-row-anim flex-wrap sm:flex-nowrap">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-[13.5px] font-semibold text-[#15161c] truncate">{r.ragione_sociale}</span>
          {badge}
        </div>
        <div className="text-[12px] text-[#9095a1] truncate mt-0.5">
          {[TIPI[r.tipo], r.servizi, dettaglio, mostraAutore ? autori[r.created_by] : null].filter(Boolean).join(" · ")}
        </div>
      </div>
      <span className="text-[13px] font-semibold scheda-font-mono text-[#3c414d] shrink-0">{r.valore ? euro(r.valore) : "—"}</span>
      {azione}
    </div>
  );
}

function Gruppo({ titolo, hint, righe, children, testId }) {
  if (!righe.length) return null;
  return (
    <div className="flex flex-col gap-1" data-testid={testId}>
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-semibold text-[#9095a1] px-3">
        {titolo}
        <span className="scheda-font-mono normal-case">({righe.length})</span>
        {hint && <InfoHint title={titolo} description={hint} />}
      </div>
      <div className="flex flex-col">{children}</div>
    </div>
  );
}

// ==================== Pagina ====================

export default function AreaCommercialePage({ user }) {
  const navigate = useNavigate();
  const direzione = eDirezione(user);
  const [mese, setMese] = useState(meseCorrente());
  const [scelto, setScelto] = useState(TEAM);
  const [dati, setDati] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errore, setErrore] = useState(false);

  const carica = useCallback(async () => {
    setLoading(true);
    setErrore(false);
    try {
      const p = new URLSearchParams({ mese });
      if (direzione && scelto !== TEAM) p.set("user_id", scelto);
      const r = await apiFetch(`/area-commerciale?${p.toString()}`);
      if (!r.ok) { setErrore(true); return; }
      setDati(await r.json());
    } catch (_) {
      setErrore(true);
    } finally {
      setLoading(false);
    }
  }, [mese, scelto, direzione]);

  useEffect(() => { carica(); }, [carica]);

  if (!puoVedereAreaCommerciale(user)) {
    return (
      <div className="py-20 text-center scheda-font-ui" data-testid="area-comm-no-access">
        <div className="text-[15px] font-semibold text-[#15161c]">Area riservata</div>
        <EmptyState>L'Area commerciale è visibile ai commerciali e alla direzione.</EmptyState>
      </div>
    );
  }

  const k = dati?.kpi;
  const ds = dati?.da_seguire;
  const vistaTeam = dati?.vista === "team";
  const autori = Object.fromEntries((dati?.commerciali || []).map((u) => [u.user_id, u.name]));
  const totaleDaSeguire = ds ? Object.values(ds).reduce((n, l) => n + l.length, 0) : 0;
  const apri = (r) => navigate(`/contratti?contratto=${r.contratto_id}`);
  const titolo = vistaTeam ? "Tutto il team" : dati?.vista === "commerciale" ? dati?.commerciale?.name : user?.name;

  return (
    <div className="flex flex-col gap-[18px] animate-fade-in scheda-font-ui" data-testid="area-commerciale-page">
      {/* Testata */}
      <div className="bg-white border border-[#e9e9f1] rounded-[18px] shadow-[0_2px_12px_rgba(0,0,0,0.03)] px-5 py-4 flex items-center gap-4 flex-wrap">
        <div className="w-[42px] h-[42px] rounded-[12px] flex items-center justify-center shrink-0" style={{ background: ICON_TILES.arancio.bg }}>
          <Briefcase className="w-5 h-5" style={{ color: ICON_TILES.arancio.color }} strokeWidth={1.85} />
        </div>
        <div className="min-w-0">
          <h1 className="text-[18px] font-bold text-[#15161c] scheda-font-ui">Area commerciale</h1>
          <div className="text-[12.5px] text-[#9095a1] truncate">{titolo || " "} · {nomeMese(mese)}</div>
        </div>
        <div className="flex items-center gap-2.5 ml-auto flex-wrap">
          {direzione && (
            <Select value={scelto} onValueChange={setScelto}>
              <SelectTrigger
                className="h-9 w-auto min-w-[200px] rounded-[10px] text-[13px] font-medium scheda-font-ui bg-white border-[#e4e4ed] hover:border-[#c9c9f0] focus:ring-0"
                data-testid="area-comm-select-commerciale"
              >
                <SelectValue placeholder="Commerciale" />
              </SelectTrigger>
              <SelectContent className="rounded-[14px] p-1.5 border border-[#e9e9f1] bg-white scheda-font-ui scheda-pop-anim z-50">
                <SelectItem value={TEAM} className="rounded-[10px] py-2 px-2.5 text-[12.5px] cursor-pointer">
                  <span className="flex items-center gap-2"><Users className="w-4 h-4 text-[#9095a1]" /> Tutto il team</span>
                </SelectItem>
                {(dati?.commerciali || []).map((u) => (
                  <SelectItem key={u.user_id} value={u.user_id} className="rounded-[10px] py-2 px-2.5 text-[12.5px] cursor-pointer">
                    <span className="flex items-center gap-2">
                      <UserAvatar name={u.name} picture={u.picture} size={20} tone={toneRuolo(u.role)} />
                      {u.name}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <MonthPicker value={mese} onChange={(v) => v && setMese(v)} data-testid="area-comm-mese" />
          <button type="button" className={`${SECONDARY_BTN} h-9`} onClick={carica} disabled={loading} data-testid="area-comm-refresh">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Aggiorna
          </button>
          <Link to="/contratti" className={`${PRIMARY_BTN_SM} h-9`} data-testid="area-comm-vai-contratti">
            <FileSignature className="w-3.5 h-3.5" /> Contratti
          </Link>
        </div>
      </div>

      {errore ? (
        <div className="py-14 text-center" data-testid="area-comm-error">
          <AlertTriangle className="w-7 h-7 mx-auto mb-2 text-[#c0392b]" />
          <div className="text-sm font-semibold text-[#15161c]">Non è stato possibile caricare l'Area commerciale</div>
          <button type="button" className={`${SECONDARY_BTN} h-8 mt-3 mx-auto`} onClick={carica}>
            <RefreshCw className="w-3.5 h-3.5" /> Riprova
          </button>
        </div>
      ) : !dati ? (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-[14px]">
          {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-[104px] rounded-[18px]" />)}
        </div>
      ) : (
        <>
          {/* KPI */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-[14px]" data-testid="area-comm-kpi">
            <Kpi
              label="Firmati nel mese"
              valore={formatNumber(k.firmati_mese)}
              sotto={<Delta ora={k.firmati_mese} prima={k.firmati_mese_precedente} />}
              testId="area-comm-kpi-firmati"
            />
            <Kpi
              label="Valore firmato"
              hint="Attivazione, quote una tantum e canone mensile per la durata dei contratti firmati nel mese. Le quote in percentuale sul fatturato del cliente non sono comprese."
              valore={euro(k.valore_mese)}
              sotto={`${euro(k.valore_anno)} da inizio ${mese.slice(0, 4)}`}
              testId="area-comm-kpi-valore"
            />
            <Kpi
              label="In attesa di firma"
              valore={formatNumber(k.in_attesa)}
              sotto={k.in_attesa ? `${euro(k.valore_in_attesa)} in ballo` : "nessun contratto inviato"}
              testId="area-comm-kpi-attesa"
            />
            <Kpi
              label="Canone in corso"
              hint="Somma dei canoni mensili dei contratti firmati oggi in vigore: il ricorrente portato che l'agenzia sta incassando."
              valore={`${euro(k.canone_in_corso)}/mese`}
              sotto={`${formatNumber(k.contratti_in_corso)} ${k.contratti_in_corso === 1 ? "contratto attivo" : "contratti attivi"}`}
              testId="area-comm-kpi-canone"
            />
            <Kpi
              label="Tasso di chiusura"
              hint="Contratti firmati su contratti inviati al cliente per la firma, da sempre. Le bozze mai inviate non contano."
              valore={k.chiusura_pct == null ? "—" : `${formatNumber(k.chiusura_pct)}%`}
              sotto={`${formatNumber(k.firmati_totali)} firmati in totale`}
              testId="area-comm-kpi-chiusura"
            />
          </div>

          {/* Andamento + provvigioni */}
          <div className={`grid gap-[18px] ${dati.provvigioni ? "lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]" : ""}`}>
            <SectionCard icon={BarChart3} iconBg={ICON_TILES.viola.bg} iconColor={ICON_TILES.viola.color} title="Valore firmato per mese" testId="area-comm-card-andamento">
              <Andamento serie={dati.andamento} mese={mese} />
            </SectionCard>
            {dati.provvigioni && (
              <SectionCard
                icon={Wallet} iconBg={ICON_TILES.verde.bg} iconColor={ICON_TILES.verde.color}
                title="Le tue provvigioni"
                action={<InfoHint title="Le tue provvigioni" description="Stesso calcolo dei Compensi, solo le tue voci commerciali. Le vedi solo tu." />}
                testId="area-comm-card-provvigioni"
              >
                <Provvigioni p={dati.provvigioni} />
              </SectionCard>
            )}
          </div>

          {/* Cose da seguire */}
          <SectionCard
            icon={CalendarClock} iconBg={ICON_TILES.arancio.bg} iconColor={ICON_TILES.arancio.color}
            title="Da seguire" count={totaleDaSeguire} testId="area-comm-card-da-seguire"
          >
            {totaleDaSeguire === 0 ? (
              <EmptyState>Niente in sospeso: tutti i contratti sono firmati, pagati e lontani dalla scadenza.</EmptyState>
            ) : (
              <div className="flex flex-col gap-5">
                <Gruppo titolo="Rinnovi da proporre" hint="Contratti che scadono entro un mese (o scaduti da poco) e non ancora rinnovati." righe={ds.rinnovi} testId="area-comm-rinnovi">
                  {ds.rinnovi.map((r) => (
                    <RigaAzione
                      key={r.contratto_id} r={r} mostraAutore={vistaTeam} autori={autori}
                      dettaglio={r.canone ? `${euro(r.canone)}/mese` : null}
                      badge={<Badge tone={r.giorni_alla_scadenza < 0 ? "danger" : "warning"} dot>
                        {r.giorni_alla_scadenza < 0 ? `Scaduto il ${data(r.scadenza)}` : `Scade il ${data(r.scadenza)}`}
                      </Badge>}
                      azione={<button type="button" className={PRIMARY_BTN_SM}
                        onClick={() => navigate(`/contratti?nuovo=rinnovo&cliente=${r.cliente_id}&rinnova=${r.contratto_id}`)}
                        data-testid={`area-comm-rinnova-${r.contratto_id}`}>Rinnova</button>}
                    />
                  ))}
                </Gruppo>
                <Gruppo titolo="In attesa di firma" hint="Il cliente ha ricevuto il link ma non ha ancora firmato." righe={ds.in_attesa} testId="area-comm-in-attesa">
                  {ds.in_attesa.map((r) => (
                    <RigaAzione
                      key={r.contratto_id} r={r} mostraAutore={vistaTeam} autori={autori}
                      dettaglio={`inviato ${giorni(r.giorni)}${r.accessi ? ` · aperto ${formatNumber(r.accessi)} ${r.accessi === 1 ? "volta" : "volte"}` : " · mai aperto"}`}
                      badge={r.link_scaduto ? <Badge tone="danger" dot>Link scaduto</Badge> : <Badge tone="warning" dot>In attesa</Badge>}
                      azione={<button type="button" className={SECONDARY_BTN} onClick={() => apri(r)}>Apri <ChevronRight className="w-3.5 h-3.5" /></button>}
                    />
                  ))}
                </Gruppo>
                <Gruppo titolo="Attivazione da pagare" hint="Contratto firmato, ma l'amministrazione non ha ancora registrato il pagamento dell'attivazione: finché manca, il cliente non parte e la tua iscrizione non matura." righe={ds.attivazioni_da_pagare} testId="area-comm-attivazioni">
                  {ds.attivazioni_da_pagare.map((r) => (
                    <RigaAzione
                      key={r.contratto_id} r={r} mostraAutore={vistaTeam} autori={autori}
                      dettaglio={`firmato ${giorni(r.giorni)}`}
                      badge={<Badge tone="warning" dot>{euro(r.attivazione)} da incassare</Badge>}
                      azione={<button type="button" className={SECONDARY_BTN} onClick={() => apri(r)}>Apri <ChevronRight className="w-3.5 h-3.5" /></button>}
                    />
                  ))}
                </Gruppo>
                <Gruppo titolo="Da inviare" hint="Contratti completati ma non ancora inviati al cliente per la firma." righe={ds.da_inviare} testId="area-comm-da-inviare">
                  {ds.da_inviare.map((r) => (
                    <RigaAzione
                      key={r.contratto_id} r={r} mostraAutore={vistaTeam} autori={autori}
                      dettaglio={`pronto ${giorni(r.giorni)}`}
                      badge={<Badge tone="accent" dot>Da inviare</Badge>}
                      azione={<button type="button" className={PRIMARY_BTN_SM} onClick={() => apri(r)}>Invia</button>}
                    />
                  ))}
                </Gruppo>
                <Gruppo titolo="Bozze" hint="Contratti iniziati e non completati." righe={ds.bozze} testId="area-comm-bozze">
                  {ds.bozze.map((r) => (
                    <RigaAzione
                      key={r.contratto_id} r={r} mostraAutore={vistaTeam} autori={autori}
                      dettaglio={`ultima modifica ${giorni(r.giorni)}`}
                      badge={<Badge tone="neutral" dot>Bozza</Badge>}
                      azione={<button type="button" className={SECONDARY_BTN} onClick={() => apri(r)}>Completa <ChevronRight className="w-3.5 h-3.5" /></button>}
                    />
                  ))}
                </Gruppo>
              </div>
            )}
          </SectionCard>

          {/* Classifica (solo team) */}
          {vistaTeam && dati.classifica.length > 0 && (
            <SectionCard icon={Trophy} iconBg={ICON_TILES.viola.bg} iconColor={ICON_TILES.viola.color} title={`Team commerciale · ${nomeMese(mese)}`} testId="area-comm-card-classifica">
              <div className="overflow-x-auto -mx-1">
                <table className="w-full min-w-[640px] text-[13px]">
                  <thead>
                    <tr className="text-[10.5px] uppercase tracking-wide text-[#9095a1] text-left">
                      <th className="font-semibold px-2 py-2">Commerciale</th>
                      <th className="font-semibold px-2 py-2 text-right">Firmati mese</th>
                      <th className="font-semibold px-2 py-2 text-right">Valore mese</th>
                      <th className="font-semibold px-2 py-2 text-right">Valore anno</th>
                      <th className="font-semibold px-2 py-2 text-right">In attesa</th>
                      <th className="font-semibold px-2 py-2 text-right">Canone in corso</th>
                      <th className="font-semibold px-2 py-2 text-right">Chiusura</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dati.classifica.map((u) => (
                      <tr key={u.user_id} className="border-t border-[#f0f0f6] hover:bg-[#fbfbfd] cursor-pointer" onClick={() => setScelto(u.user_id)} data-testid={`area-comm-classifica-${u.user_id}`}>
                        <td className="px-2 py-2.5">
                          <span className="flex items-center gap-2.5">
                            <UserAvatar name={u.name} picture={u.picture} size={26} tone={toneRuolo(u.role)} />
                            <span className="font-semibold text-[#15161c]">{u.name}</span>
                          </span>
                        </td>
                        <td className="px-2 py-2.5 text-right scheda-font-mono">{formatNumber(u.firmati_mese)}</td>
                        <td className="px-2 py-2.5 text-right scheda-font-mono font-semibold text-[#15161c]">{euro(u.valore_mese)}</td>
                        <td className="px-2 py-2.5 text-right scheda-font-mono">{euro(u.valore_anno)}</td>
                        <td className="px-2 py-2.5 text-right scheda-font-mono">{formatNumber(u.in_attesa)}</td>
                        <td className="px-2 py-2.5 text-right scheda-font-mono">{euro(u.canone_in_corso)}</td>
                        <td className="px-2 py-2.5 text-right scheda-font-mono">{u.chiusura_pct == null ? "—" : `${formatNumber(u.chiusura_pct)}%`}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </SectionCard>
          )}

          {/* Clienti */}
          <SectionCard icon={Users} iconBg={ICON_TILES.teal.bg} iconColor={ICON_TILES.teal.color} title="Clienti firmati" count={dati.clienti.length} testId="area-comm-card-clienti">
            {dati.clienti.length === 0 ? (
              <EmptyState>Ancora nessun contratto firmato.</EmptyState>
            ) : (
              <div className="flex flex-col">
                {dati.clienti.map((c) => (
                  <div key={c.client_id} className="grid grid-cols-1 md:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_110px_120px_170px] gap-2 md:gap-4 items-center px-3 py-3 rounded-[10px] hover:bg-[#fbfbfd] border-b border-[#f0f0f6] last:border-b-0" data-testid={`area-comm-cliente-${c.client_id}`}>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 min-w-0">
                        {direzione ? (
                          <Link to={`/scheda-cliente/${c.client_id}`} className="text-[13.5px] font-semibold text-[#15161c] hover:text-[#5b5bd6] truncate">{c.nome}</Link>
                        ) : (
                          <span className="text-[13.5px] font-semibold text-[#15161c] truncate">{c.nome}</span>
                        )}
                        {!c.attivo && <Badge tone="neutral">Non attivo</Badge>}
                      </div>
                      <div className="text-[12px] text-[#9095a1] mt-0.5">
                        {c.portato ? "Nuovo cliente" : "Cliente già esistente"} · prima firma {data(c.prima_firma)}
                        {c.contratti > 1 ? ` · ${formatNumber(c.contratti)} contratti` : ""}
                      </div>
                    </div>
                    <div>
                      {c.attivazione_pagata
                        ? <Badge tone="success" dot>Attivazione pagata</Badge>
                        : <Badge tone="warning" dot>Attivazione da pagare</Badge>}
                    </div>
                    <div className="md:text-right">
                      <div className="text-[10.5px] uppercase tracking-wide text-[#9095a1] font-semibold">Canone</div>
                      <div className="text-[13px] font-semibold scheda-font-mono text-[#15161c]">{c.canone_in_corso ? `${euro(c.canone_in_corso)}/m` : "—"}</div>
                    </div>
                    <div className="md:text-right">
                      <div className="text-[10.5px] uppercase tracking-wide text-[#9095a1] font-semibold">Valore firmato</div>
                      <div className="text-[13px] font-semibold scheda-font-mono text-[#15161c]">{euro(c.valore_totale)}</div>
                    </div>
                    <div className="min-w-0">
                      {c.scadenza ? <BarraScadenza inizio={c.inizio} scadenza={c.scadenza} /> : <span className="text-[12px] text-[#9095a1]">Nessun contratto in corso</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </SectionCard>
        </>
      )}
    </div>
  );
}
